//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { TypeHelper } from '../../../helpers'

import { IMRAccordionItemController } from './components/accordion-item/accordion-item.controller'


// Public Types

export interface IIMRAccordion {
    enableAll(): void
    disableAll(): void
    enableToolbarAll(): void
    disableToolbarAll(): void
}

export type IMRAccordionOptions = {
    disableCollapsing?: boolean
    onInitialized?(accordionComponent: IIMRAccordion): void
    onStateRestoring?(): any
    onStateStoring?(state: any): void
}

// Public Types End


// Private Types

type AccordionState = {
    expandedNames: string[]
}

type AccordionItem = {
    disabled: boolean
    expanded: boolean
    isToolbarVisible: boolean
    name: string
    toolbarOptions: DevExpress.ui.dxToolbarOptions
}

// Private Types End


export class IMRAccordionController implements ng.IComponentController, IIMRAccordion {

    // Members

    private options: IMRAccordionOptions

    private accordion: DevExpress.ui.dxAccordion
    private accordionOptions: DevExpress.ui.dxAccordionOptions

    private accordionItemControllers: IMRAccordionItemController[]
    private accordionItemsCount: number
    private isSingleItemAccordion: boolean

    // Members End


    // Ctor

    constructor(private $element: JQuery, private $timeout: ng.ITimeoutService) { }

    // Ctor End


    // IIMRAccordion Implementation

    public enableAll(): void {
        this.accordionItemControllers.map((accordionItemController: IMRAccordionItemController) => {
            accordionItemController.enable()
        })
    }

    public disableAll(): void {
        this.accordionItemControllers.map((accordionItemController: IMRAccordionItemController) => {
            accordionItemController.disable()
        })
    }

    public enableToolbarAll(): void {
        this.accordionItemControllers.map((accordionItemController: IMRAccordionItemController) => {
            accordionItemController.enableToolbar()
        })
    }

    public disableToolbarAll(): void {
        this.accordionItemControllers.map((accordionItemController: IMRAccordionItemController) => {
            accordionItemController.disableToolbar()
        })
    }

    // IIMRAccordion Implementation End


    // IComponentController Implementation 

    public $onInit(): void {
        this.accordionItemControllers = []
        this.accordionItemsCount = 0
    }

    public $postLink(): void {
        this.initialize()
    }

    // IComponentController Implementation End


    // Component

    private initialize(): void {
        this.setDefaults()

        this.initializeItems()
            .then(() => {
                return this.initializeAccordion()
            })
            .then(() => {
                this.onInitialized()
                this.onInitializedAll()
            })
    }

    // Component End


    // Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onInitializedAll(): void {
        this.accordionItemControllers.map((accordionItemController: IMRAccordionItemController) => {
            accordionItemController.onInitialized()
        })
    }

    private onStateStoring(state): void {
        if (this.options != null && _.isFunction(this.options.onStateStoring)) {
            this.options.onStateStoring(state)
        }
    }

    private onStateRestoring(): any {
        if (this.options != null && _.isFunction(this.options.onStateRestoring)) {
            return this.options.onStateRestoring()
        }

        return null
    }

    // Events End


    // Parent/Child Communication

    public getNextAccordionItemName(): string {
        return `accordionItem${this.accordionItemsCount++}`
    }

    public registerAccordionItem(accordionItemController: IMRAccordionItemController): void {
        this.accordionItemControllers.push(accordionItemController)
    }

    public updateItems(): void {
        if (this.accordion != null) {
            this.accordion.option('items', this.getDxItemData())
        }
    }

    private getItemControllerByName(itemName: string): IMRAccordionItemController {
        return _.find(this.accordionItemControllers, (itemController: IMRAccordionItemController) => itemController.name === itemName)
    }

    private initializeItems(): JQueryPromise<void> {
        return $.when(...this.accordionItemControllers.map((accordionItemController) => accordionItemController.isInitialized))
    }

    // Parent/Child Communication End


    // Accordion

    private onAccordionInitialized(e: any): void {
        this.accordion = e.component
        this.expandItems()
    }

    private onAccordionContentReady(e: any): void {
        if (this.accordion != null) {

            // Timeout because some widgets do not fit the container
            this.$timeout(() => {
                this.accordion.updateDimensions()
            }, 200)
        }
    }

    private onAccordionItemRendered(e: any): void {
        e.itemElement.addClass('imr-accordion__item')

        if (e.itemData != null && !e.itemData.isToolbarVisible) {
            e.itemElement.addClass('imr-accordion__item_no-toolbar')
        }
    }

    private onAccordionSelectionChanged(e: any): void {
        if (this.accordion != null) {
            _.forEach(e.addedItems, (item: AccordionItem) => {
                item.expanded = true

                const itemController: IMRAccordionItemController = this.getItemControllerByName(item.name)

                if (itemController != null) {
                    itemController.isExpanded = true
                }
            })

            _.forEach(e.removedItems, (item: AccordionItem) => {
                item.expanded = false


                const itemController: IMRAccordionItemController = this.getItemControllerByName(item.name)

                if (itemController != null) {
                    itemController.isExpanded = false
                }
            })

            const items: AccordionItem[] = this.accordion.option('items')
            const expandedNames: string[] = _
                .chain(items)
                .filter((item: AccordionItem) => item.expanded)
                .map((item: AccordionItem) => item.name)
                .value()

            this.storeStateOption({
                expandedNames
            })
        }
    }

    private onAccordionTitleClick(e: any): void {
        if (this.options != null && this.options.disableCollapsing === true) {
            e.event.stopPropagation()
        }
    }

    private initializeAccordion(): JQueryPromise<any> {
        this.accordionOptions = {
            focusStateEnabled: false,
            height: '100%',
            items: this.getDxItemData(),
            multiple: true,

            itemTemplate: (itemData: any, itemIndex: number, $itemElement: JQuery) => this.itemTemplateFunc(itemData, itemIndex, $itemElement),

            onInitialized: (e: any) => this.onAccordionInitialized(e),
            onContentReady: (e: any) => this.onAccordionContentReady(e),
            onItemRendered: (e: any) => this.onAccordionItemRendered(e),
            onSelectionChanged: (e: any) => this.onAccordionSelectionChanged(e),
            onItemTitleClick: (e: any) => this.onAccordionTitleClick(e),
        }

        return TypeHelper.onInitializedAll(this.accordionOptions)
    }

    private expandItems(): void {
        if (this.accordion != null) {
            const items: AccordionItem[] = this.accordion.option('items')

            _.forEach(items, (item: AccordionItem, index: number) => {
                item.expanded ? this.accordion.expandItem(index) : this.accordion.collapseItem(index)

                const itemController: IMRAccordionItemController = this.getItemControllerByName(item.name)

                if (itemController != null) {
                    itemController.isExpanded = item.expanded
                }
            })
        }
    }

    private getDxItemData(): AccordionItem[] {
        return this.accordionItemControllers.map((accordionItemController: IMRAccordionItemController) => {
            const itemData: AccordionItem = {
                disabled: accordionItemController.isDisabled,
                name: accordionItemController.name,
                isToolbarVisible: accordionItemController.isToolbarVisible,
                toolbarOptions: accordionItemController.toolbarOptions,
                expanded: accordionItemController.isExpanded
            }

            const expandedState: string[] = this.getState().expandedNames

            if (_.isArray(expandedState)) {
                itemData.expanded = _.contains(expandedState, itemData.name)
            }

            return itemData
        })
    }

    private itemTemplateFunc(itemData: any, itemIndex: number, $itemElement: JQuery) {
        const $accordionItemElement: JQuery = this.$element.find('#' + itemData.name)

        if ($accordionItemElement != null) {
            $accordionItemElement.css('display', 'block')
            $itemElement.append($accordionItemElement)
        }
    }

    // Accordion End


    // Options

    private setDefaults(): void {
        this.isSingleItemAccordion = this.accordionItemControllers.length === 1
    }

    // Options End


    // State

    private getState(): AccordionState {
        return this.onStateRestoring() || {
            expandedNames: null
        }
    }

    private storeStateOption(state: any): void {
        const existingState: AccordionState = this.getState()

        this.onStateStoring(_.assign(existingState, state))
    }

    // State End
}
